import React, { useContext, useState, useMemo } from "react";
import { useFirstName } from "../hooks/useFirstName";
import { ThemedIcon } from "@me8eon/icons";
import { useTranslation } from "@me8eon/translation";
import AiAssistCard from "../welcome-components/ai-assist-card/AiAssistCard";
import { AiAssistContext } from "../AiAssist.context";

type Language = "en" | "de" | "sv" | "ro" | "pl" | "it" | "nl" | "hu" | "cz";

interface Issue {
    id: number;
    question: string;
}

const serviceIssueList: Record<Language, Issue[]> = {
    en: [
        { id: 1, question: "I forgot my password. How can I reset it?" },
        { id: 2, question: "I'm having trouble sending/receiving emails. What should I do?" },
        { id: 3, question: "What is MyHelp?" },
    ],
    de: [
        { id: 1, question: "Ich habe mein Passwort vergessen. Wie kann ich es zurücksetzen?" },
        { id: 2, question: "Ich habe Probleme beim Senden/Empfangen von E-Mails. Was sollte ich tun?" },
        { id: 3, question: "Was ist MyHelp?" },
    ],
    sv: [
        { id: 1, question: "Jag har glömt mitt lösenord. Hur kan jag återställa det?" },
        { id: 2, question: "Jag har problem med att skicka/motta e-post. Vad ska jag göra?" },
        { id: 3, question: "Vad är MyHelp?" },
    ],
    ro: [
        { id: 1, question: "Am uitat parola. Cum o pot reseta?" },
        { id: 2, question: "Am probleme cu trimiterea/primirea e-mailurilor. Ce ar trebui să fac?" },
        { id: 3, question: "Ce este MyHelp?" },
    ],
    it: [
        {
            "id": 1,
            "question": "Ho dimenticato la mia password. Come posso reimpostarla?"
        },
        {
            "id": 2,
            "question": "Sto riscontrando problemi nell'invio/ricezione delle email. Cosa dovrei fare?"
        },
        {
            "id": 3,
            "question": "Cos'è MyHelp?"
        }
    ],
    nl: [
        {
            "id": 1,
            "question": "Ik ben mijn wachtwoord vergeten. Hoe kan ik het opnieuw instellen?"
        },
        {
            "id": 2,
            "question": "Ik heb problemen met het verzenden/ontvangen van e-mails. Wat moet ik doen?"
        },
        {
            "id": 3,
            "question": "Wat is MyHelp?"
        }
    ],
    hu: [
        {
            "id": 1,
            "question": "Elfelejtettem a jelszavamat. Hogyan tudom visszaállítani?"
        },
        {
            "id": 2,
            "question": "Problémám van az e-mailek küldésével/fogadásával. Mit tegyek?"
        },
        {
            "id": 3,
            "question": "Mi az a MyHelp?"
        }
    ],
    cz: [
        {
            "id": 1,
            "question": "Zapomněl jsem heslo. Jak ho mohu resetovat?"
        },
        {
            "id": 2,
            "question": "Mám potíže s odesíláním/přijímáním e-mailů. Co bych měl dělat?"
        },
        {
            "id": 3,
            "question": "Co je MyHelp?"
        }
    ],
    pl: [
        {
            "id": 1,
            "question": "Zapomniałem hasła. Jak mogę je zresetować?"
        },
        {
            "id": 2,
            "question": "Mam problem z wysyłaniem/odbieraniem e-maili. Co powinienem zrobić?"
        },
        {
            "id": 3,
            "question": "Co to jest MyHelp?"
        }
    ],
};

const internetIssueList: Record<Language, Issue[]> = {
    en: [
        { id: 1, question: "Suggest some strategies to improve my work-life balance" },
        { id: 2, question: "What green energy initiatives is E.on currently taking?" },
        { id: 3, question: "Suggest some strategies to improve my time management at work" },
    ],
    de: [
        { id: 1, question: "Suggest some strategies to improve my work-life balance" },
        { id: 2, question: "What green energy initiatives is E.on currently taking?" },
        { id: 3, question: "Suggest some strategies to improve my time management at work" },
    ],
    sv: [
        { id: 1, question: "Suggest some strategies to improve my work-life balance" },
        { id: 2, question: "What green energy initiatives is E.on currently taking?" },
        { id: 3, question: "Suggest some strategies to improve my time management at work" },
    ],
    ro: [
        { id: 1, question: "Sugerează câteva strategii pentru a-mi îmbunătăți echilibrul dintre viața profesională și cea personală" },
        { id: 2, question: "Ce inițiative de energie verde desfășoară în prezent E.on?" },
        { id: 3, question: "Sugerează câteva strategii pentru a-mi îmbunătăți gestionarea timpului la locul de muncă" },
    ],
    it: [
        {
            "id": 1,
            "question": "Ho dimenticato la mia password. Come posso reimpostarla?"
        },
        {
            "id": 2,
            "question": "Sto riscontrando problemi nell'invio/ricezione delle email. Cosa dovrei fare?"
        },
        {
            "id": 3,
            "question": "Cos'è MyHelp?"
        }
    ],
    nl: [
        {
            "id": 1,
            "question": "Ik ben mijn wachtwoord vergeten. Hoe kan ik het opnieuw instellen?"
        },
        {
            "id": 2,
            "question": "Ik heb problemen met het verzenden/ontvangen van e-mails. Wat moet ik doen?"
        },
        {
            "id": 3,
            "question": "Wat is MyHelp?"
        }
    ],
    hu: [
        {
            "id": 1,
            "question": "Elfelejtettem a jelszavamat. Hogyan tudom visszaállítani?"
        },
        {
            "id": 2,
            "question": "Problémám van az e-mailek küldésével/fogadásával. Mit tegyek?"
        },
        {
            "id": 3,
            "question": "Mi az a MyHelp?"
        }
    ],
    cz: [
        {
            "id": 1,
            "question": "Zapomněl jsem heslo. Jak ho mohu resetovat?"
        },
        {
            "id": 2,
            "question": "Mám potíže s odesíláním/přijímáním e-mailů. Co bych měl dělat?"
        },
        {
            "id": 3,
            "question": "Co je MyHelp?"
        }
    ],
    pl: [
        {
            "id": 1,
            "question": "Zapomniałem hasła. Jak mogę je zresetować?"
        },
        {
            "id": 2,
            "question": "Mam problem z wysyłaniem/odbieraniem e-maili. Co powinienem zrobić?"
        },
        {
            "id": 3,
            "question": "Co to jest MyHelp?"
        }
    ],
};

interface AiWelcomeWrapperProps {
    eonInternetCardKey: string;
}

const AiWelcomeWrapper: React.FC<AiWelcomeWrapperProps> = ({ eonInternetCardKey }) => {
    const firstName = useFirstName();
    const [currentCardKey, setCurrentCardKey] = useState<number>(0);
    const { t, i18n } = useTranslation();
    const { handleAiQuery } = useContext(AiAssistContext);

    const issues = useMemo(() => {
        const lang = i18n.language as Language;
        return eonInternetCardKey === "selfService"
            ? serviceIssueList[lang]
            : internetIssueList[lang];
    }, [eonInternetCardKey, i18n.language]);

    const handleCardClick = (issue: Issue) => () => {
        setCurrentCardKey(issue.id);
        handleAiQuery(issue.question);
    };

    return (
        <>
            <div className="ai-assist-welcome">
                <ThemedIcon name="AiIcon64" />
                <div>
                    <span
                        title={t("chatBot.topicStartMessage")}
                        className="start-message"
                    >
                        {t("chatBot.hey")} {firstName},{" "}
                        {t("chatBot.topicStartMessage")}
                    </span>
                </div>
            </div>

            <div className="card-container">
                {issues.map((issue) => (
                    <AiAssistCard
                        key={issue.id}
                        message={issue.question}
                        currentCardSetter={handleCardClick(issue)}
                    />
                ))}
            </div>
        </>
    );
};

export default AiWelcomeWrapper;

